import {
  ICreateNoteRequest,
  ICreateProductPurchaseRequest,
  ICreateProductReplenishRequest,
  ICreateProductTagRequest,
  ICreateTagRequest,
  IDeleteNoteRequest,
  IDeleteProductTagRequest,
  IGetProductHistoriesRequest,
  IGetProductHistoriesResponse,
  IUpdateLookupRequest,
  IUpdateNoteRequest,
  IUpdateProductRequest,
  IListOnAmazonRequest,
  IGetAppConfigResponse,
  IUpdateTagRequest,
  TDeleteUploadedFilePayload,
  TGetUploadsListPayload,
  TGetUploadsListResponse,
  TUploadedFile,
  TSendFileFormattedDataPayload,
  TSendFileFormattedDataResponse,
  TUploadFileResponse,
  TUploadsStats,
  TRegisterPayload,
  TRegisterResponse,
  TLoginPayload,
  TResetPasswordPayload,
  TLoginResponse,
  TSetNewPasswordPayload,
  TVerifyEmailPayload,
  TVerifyEmailResponse,
  TResendVerificationEmailPayload,
  IGetLookupsRequest,
  TGetLookupsResponse,
  TChangePasswordPayload,
  TProductsSearchResult,
  TVerifyNewEmailPayload,
  TGetBillingHistoryRes,
  TGetBillingHistoryPayload,
  TGetPaymentMethodsRes,
  TGetPaymentMethodsPayload,
  TGetPricingPlansPayload,
  TGetPricingPlansRes,
  TGetCheckoutLinkPayload,
  TGetProductQuickScanRequest,
  TGetSettingsResponse,
  TUpdateSettingsRequest,
  TGetCheckoutLinkRes,
  TGetPaymentMethodLinkPayload,
  TGetPaymentMethodLinkRes,
  TGetCancelSubscriptionLinkPayload,
  TGetCancelSubscriptionLinkRes,
  TGetStripeSessionDetailsPayload,
  TGetStripeSessionDetailsRes,
  TGetSubscriptionCouponsPayload,
  TGetSubscriptionCouponsRes,
  TGetBuyBoxAnalysisParams,
  TGetBuyBoxAnalysisResponse,
  TGetSellersResponse,
  // TGetOffersParams,
  // TGetOffersResponse,
} from "./apiV2.types";
import { TUser } from "../features/user/user.types";
import { ILookup } from "../features/lookups/lookups.types";
import { axiosInstance } from "./config";
import { TOffer, TRestrictions } from "../features/productAnalyzer/productAnalyzer.types";

/* Product APIs */

/**
 * Retrieves quick scan information for a product using various identifiers
 * @param {Object} params - Request parameters
 * @param {string} params.asin - ASIN (Amazon Standard Identification Number)
 * @param {string} [params.lookupId] - Optional ID to use for product lookup
 */
export const getProductQuickScan = ({
  asin,
  lookupId,
}: TGetProductQuickScanRequest) => {
  return axiosInstance.get(`/Analyzer/${asin}/QuickScan`, {
    params: { lookupId },
  });
};

export const getAmazonRestrictions = (asin: string) => {
  return axiosInstance.get<TRestrictions>(`/Amazon/${asin}/Restrictions`);
};

/**
 * Performs a search query to retrieve product results
 * @param q The search term to query products. Query can be:
 *   - ASIN (Amazon Standard Identification Number)
 *   - UPC (Universal Product Code)
 *   - ISBN (International Standard Book Number)
 *   - Keyword (Search term for product)
 * @param page The page number for paginated results
 */
export const getSearchResults = (q: string, page = 0) => {
  return axiosInstance.get<TProductsSearchResult[]>(`/search`, {
    params: { q, page },
  });
};

// export const fetchProductPurchases = (asin: string) => {
//   return axiosInstance.get(`/products/${asin}/purchases`);
// };
//
// export const fetchProductReplenishes = (asin: string) => {
//   return axiosInstance.get(`/products/${asin}/replenishes`);
// };

export const createProductPurchase = ({
  asin,
  quantity,
  salePrice,
  costPrice,
}: ICreateProductPurchaseRequest) => {
  return axiosInstance.post(`/products/${asin}/purchases`, {
    quantity,
    salePrice,
    costPrice,
  });
};

export const createProductReplenish = ({
  asin,
  quantity,
  salePrice,
  costPrice,
}: ICreateProductReplenishRequest) => {
  return axiosInstance.post(`/products/${asin}/replenishes`, {
    quantity,
    salePrice,
    costPrice,
  });
};

export const updateProduct = ({ asin, isFavorite }: IUpdateProductRequest) => {
  return axiosInstance.post(`/products/${asin}`, {
    isFavorite,
  });
};

export const getAppConfig = () => {
  return axiosInstance.get<IGetAppConfigResponse>("/AppConfig");
};

export const listOnAmazon = ({
  asin,
  quantity,
  price,
}: IListOnAmazonRequest) => {
  return axiosInstance.put(
    `/Amazon/${asin}/ListOnAmazon/${quantity}/${price}`,
  );
};

export const getProductHistories = ({
  asin,
  page,
  perPage,
}: IGetProductHistoriesRequest) => {
  return axiosInstance.get<IGetProductHistoriesResponse>(
    `/products/${asin}/histories`,
    {
      params: {
        page,
        perPage,
      },
    },
  );
};

export const getProductTags = (asin: string) => {
  return axiosInstance.get(`/products/${asin}/tags`);
};

export const createProductTag = ({ asin, tagId }: ICreateProductTagRequest) => {
  return axiosInstance.post(`/products/${asin}/tags/${tagId}`);
};

export const deleteProductTag = ({ asin, tagId }: IDeleteProductTagRequest) => {
  return axiosInstance.delete(`/products/${asin}/tags/${tagId}`);
};

/** Note APIs */
export const fetchNotes = (asin: string) => {
  return axiosInstance.get(`/products/${asin}/notes`);
};

export const deleteNote = ({ id, asin }: IDeleteNoteRequest) => {
  return axiosInstance.delete(`/products/${asin}/notes/${id}`);
};

export const createNote = ({ asin, note }: ICreateNoteRequest) => {
  return axiosInstance.post(`/products/${asin}/notes`, { note });
};

export const updateNote = ({ id, asin, note }: IUpdateNoteRequest) => {
  return axiosInstance.put(`/products/${asin}/notes/${id}`, {
    note,
  });
};

/** Settings APIs */
export const getTags = () => {
  return axiosInstance.get("/tags");
};

export const createTag = ({ caption }: ICreateTagRequest) => {
  return axiosInstance.post(`/tags`, { caption });
};

export const updateTag = ({ id, caption }: IUpdateTagRequest) => {
  return axiosInstance.put(`/tags/${id}`, { caption });
};

export const deleteTag = (id: string) => {
  return axiosInstance.delete(`/tags/${id}`);
};

export const getSettings = () => {
  return axiosInstance.get<TGetSettingsResponse>("/analyzer/settings");
};

export const updateSettings = (req: TUpdateSettingsRequest) => {
  return axiosInstance.put("/analyzer/settings", req);
};

/** Lookup APIs */
export const getLookups = (params: IGetLookupsRequest) => {
  return axiosInstance.get<TGetLookupsResponse>(`/lookups`, {
    params,
  });
};

export const getLookup = (id: string) => {
  return axiosInstance.get<ILookup>(`/lookups/${id}`);
};

export const updateLookup = ({ id, ...params }: IUpdateLookupRequest) => {
  return axiosInstance.put(`/lookups/${id}`, { ...params, countryCode: "US" });
};

/** ProductUpload APIs */
export const getUploadsList = async (payload: TGetUploadsListPayload) => {
  const res = await axiosInstance.get<TGetUploadsListResponse>(
    `/ProductUpload/UploadList`,
    { params: payload },
  );
  return res.data;
};

export const deleteUploadedFile = (payload: TDeleteUploadedFilePayload) => {
  return axiosInstance.delete(`/ProductUpload/DeleteProductUpload`, {
    data: payload,
  });
};

export const getUploadsStats = async () => {
  const res = await axiosInstance.get<TUploadsStats>(`/ProductUpload/summary`);
  return res.data;
};

export const getUploadedFile = async (id: TUploadedFile["productUploadId"]) => {
  return axiosInstance.get<Blob>(`/ProductUpload/${id}/ExportProducts`, {
    responseType: "blob",
  });
};

export const uploadFile = (payload: FormData) => {
  return axiosInstance.post<TUploadFileResponse>(
    `/ProductUpload/UploadFile`,
    payload,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
};

export const sendFileFormattedData = (
  payload: TSendFileFormattedDataPayload,
) => {
  return axiosInstance.post<TSendFileFormattedDataResponse>(
    `/ProductUpload/AddProductUpload`,
    payload,
  );
};

/* Auth APIs */
export const register = (payload: TRegisterPayload) => {
  return axiosInstance.post<TRegisterResponse>(`/Account/Register`, payload);
};

export const login = (payload: TLoginPayload) => {
  return axiosInstance.post<TLoginResponse>(`/Account/Login`, payload);
};

export const logout = () => {
  return axiosInstance.post(`/account/logout`);
};

export const resetPassword = (payload: TResetPasswordPayload) => {
  return axiosInstance.post<boolean>(`/Account/ForgotPassword`, payload);
};

export const setNewPassword = (payload: TSetNewPasswordPayload) => {
  return axiosInstance.post(`/Account/ResetPassword`, payload);
};

export const verifyEmail = (payload: TVerifyEmailPayload) => {
  return axiosInstance.post<TVerifyEmailResponse>(
    `/Account/VerifyEmail`,
    payload,
  );
};

export const resendVerificationEmail = (
  payload: TResendVerificationEmailPayload,
) => {
  return axiosInstance.post<boolean>(
    `/Account/ResendEmailVerification`,
    payload,
  );
};

/* Account Settings API */
export const changePassword = (payload: TChangePasswordPayload) => {
  return axiosInstance.post<{}>(`/account/updatePassword`, payload);
};

export const getUserProfile = () => {
  return axiosInstance.get<TUser>(`/account/profile`);
};

export const updateUserProfile = (payload: FormData) => {
  return axiosInstance.put<TUser>(`/account/profile`, payload, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const verifyNewEmail = (payload: TVerifyNewEmailPayload) => {
  return axiosInstance.post(`/account/verifyEmailChange`, payload);
};

/* Billing APIs */
export const getBillingHistory = (payload: TGetBillingHistoryPayload) => {
  const { perPage, lastInvoiceId, config } = payload;
  return axiosInstance.get<TGetBillingHistoryRes>("/Account/billing-history", {
    params: { perPage, lastInvoiceId },
    ...config,
  });
};

export const getPaymentMethods = (payload?: TGetPaymentMethodsPayload) => {
  return axiosInstance.get<TGetPaymentMethodsRes>(
    "/Account/payment-methods",
    payload?.config,
  );
};

export const getPricingPlans = (payload?: TGetPricingPlansPayload) => {
  return axiosInstance.get<TGetPricingPlansRes>(
    "/Account/plans",
    payload?.config,
  );
};

export const getCheckoutLink = ({
  planId,
  couponId,
  redirectUrl,
}: TGetCheckoutLinkPayload) => {
  return axiosInstance.post<TGetCheckoutLinkRes>(
    `/subscription/${planId}/changeproduct`,
    {
      redirectUrl,
    },
    {
      params: {
        ...(couponId && { CouponId: couponId }),
      },
    },
  );
};

export const getPaymentMethodLink = (
  payload?: TGetPaymentMethodLinkPayload,
) => {
  return axiosInstance.get<TGetPaymentMethodLinkRes>(
    `/subscription/manage-payment-method-link`,
    payload?.config,
  );
};

export const getCancelSubscriptionLink = (
  payload?: TGetCancelSubscriptionLinkPayload,
) => {
  return axiosInstance.get<TGetCancelSubscriptionLinkRes>(
    `/Subscription/cancel-subscription-link`,
    payload?.config,
  );
};

export const getStripeSessionDetails = ({
  config,
  sessionId,
}: TGetStripeSessionDetailsPayload) => {
  return axiosInstance.get<TGetStripeSessionDetailsRes>(
    `/subscription/checkout-sessions/${sessionId}`,
    config,
  );
};

export const getSubscriptionCoupons = (
  payload?: TGetSubscriptionCouponsPayload,
) => {
  return axiosInstance.get<TGetSubscriptionCouponsRes>(
    `/subscription/coupons`,
    payload?.config,
  );
};


// export const getBuyBoxAnalysis = ({
//   asin,
//   range,
// }: TGetBuyBoxAnalysisParams) => {
//   return axiosInstance.get<TGetBuyBoxAnalysisResponse[]>(
//     `/Analyzer/${asin}/BuyBoxAnalysis`,
//     {
//       params: {
//         range,
//       },
//     },
//   );
// };

export const getSellers = (ids: string[]) => {
  return axiosInstance.post<{ sellers: TGetSellersResponse[] }>(`/sellers/ids`, ids);
};

export const updateAmazonCrendetials = (params: {
  spapi_oauth_code: string;
  selling_partner_id: string;
  state: string;
}) => {
  return axiosInstance.post("/oauth/authorize", params);
};

export const getAmazonCredentials = () => {
  return axiosInstance.get<import("./apiV2.types").TAmazonCredentialsResponse>("/Amazon/Credentials");
};
